/**
 * Uk Designer — scroll reveal.
 *
 * Elements marked `data-pantry-reveal` fade and rise into place once, the first
 * time they enter the viewport. Siblings sharing a `data-pantry-reveal-group`
 * parent are staggered so a grid resolves as a wave rather than all at once.
 *
 * Progressive enhancement: the CSS that hides elements is gated behind the
 * `pantry-reveal-ready` class, which this script adds. If the script never runs
 * — or IntersectionObserver is missing — nothing is ever hidden.
 */
(() => {
  const SELECTOR = '[data-pantry-reveal]';
  const STAGGER_MS = 90;
  const MAX_STAGGER_MS = 480;

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** Reveal everything immediately, with no transition. */
  const revealAll = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach((el) => el.classList.add('is-revealed'));
  };

  const assignStagger = (root = document) => {
    root.querySelectorAll('[data-pantry-reveal-group]').forEach((group) => {
      const items = group.querySelectorAll(`:scope > ${SELECTOR}`);
      items.forEach((el, i) => {
        if (el.style.getPropertyValue('--pantry-reveal-delay')) return;
        const delay = Math.min(i * STAGGER_MS, MAX_STAGGER_MS);
        el.style.setProperty('--pantry-reveal-delay', `${delay}ms`);
      });
    });
  };

  let observer = null;

  const observe = (root = document) => {
    if (!observer) return;
    root.querySelectorAll(SELECTOR).forEach((el) => {
      if (el.classList.contains('is-revealed')) return;
      observer.observe(el);
    });
  };

  const init = () => {
    // No observer support, or the visitor asked for less motion: show everything.
    if (!('IntersectionObserver' in window) || prefersReducedMotion.matches) {
      revealAll();
      return;
    }

    document.documentElement.classList.add('pantry-reveal-ready');
    assignStagger();

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      {
        // Trigger a little before the element's top edge reaches the fold, so
        // the motion finishes about when it's comfortably in view.
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.08,
      }
    );

    observe();
  };

  // If the visitor turns reduced-motion on mid-session, stop animating.
  prefersReducedMotion.addEventListener('change', (e) => {
    if (e.matches) revealAll();
  });

  // Theme editor re-renders sections without a page load — re-scan on each.
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target || document;
    if (!('IntersectionObserver' in window) || prefersReducedMotion.matches) {
      revealAll(root);
      return;
    }
    assignStagger(root);
    observe(root);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
